import { Space, Tooltip, Typography } from "antd"
import { roundToPrecision } from "../../../../helpers/math"
import { centsToDollarsDisplay } from "../../../displayFormatters"
import { pluralize } from "../../../../helpers/strings"
import { PricingType, PricingTypes } from "../../../../common/constants"
import { faArrowCircleDown, faArrowCircleUp } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { hasValue } from "../../../../helpers/nullable"

const { Text } = Typography

type Props = {
  finalPrice: number
  estimatedPrice: number
  estimatedHours: number
  hideFinalPrice?: boolean
  estimatedPricePerVisit?: number
  estimatedHoursPerVisit?: number
  pricingType: PricingType
  customQuotedPrice?: number | null
}

const PriceSummary = ({finalPrice, estimatedPrice, estimatedHours, hideFinalPrice, estimatedPricePerVisit, estimatedHoursPerVisit, pricingType, customQuotedPrice}: Props) => {
  return  <Space direction="vertical" size="small" align="end">
    {!hideFinalPrice &&
      <span>
        {buildPriceDisplay(pricingType, finalPrice, customQuotedPrice, estimatedPrice)}&nbsp;
        <FinalPriceDifferenceIcon pricingType={pricingType} customQuotedPrice={customQuotedPrice} estimatedPrice={estimatedPrice} estimatedPricePerVisit={estimatedPricePerVisit} />
      </span>
    }
    <Text type="secondary">
      {buildEstimatedDisplay(pricingType, estimatedPrice, estimatedHours, estimatedHoursPerVisit)}
    </Text>
  </Space>
}

export default PriceSummary

const displayHours = (hours: number) => `${roundToPrecision(hours, 1)} ${pluralize(hours, 'hour')}`

const buildPriceDisplay = (pricingType: PricingType, finalPrice: number, customQuotedPrice: number | null | undefined, estimatedPrice: number) => {
  if (pricingType === PricingTypes.PerVisit) {
    return <><Text type="secondary">Per Visit</Text> <strong>{centsToDollarsDisplay(customQuotedPrice ?? estimatedPrice)}</strong></>
  }
  return <><Text type="secondary">Total</Text> <strong>{centsToDollarsDisplay(finalPrice)}</strong></>
}

const buildEstimatedDisplay = (pricingType: PricingType, estimatedPrice: number, estimatedHours: number, estimatedHoursPerVisit?: number) => {
  switch (pricingType) {
    case PricingTypes.PerVisit:
      return <small>Est. {displayHours(estimatedHoursPerVisit ?? 0)}</small>
    case PricingTypes.TimeAndMaterials:
    case PricingTypes.FlatRate:
      return <small>Est. {centsToDollarsDisplay(estimatedPrice)} | {displayHours(estimatedHours)}</small>
    default:
      return <small>Est. {displayHours(estimatedHours)}</small>
  }
}


type FinalPriceDifferenceIconProps = {
  pricingType: PricingType
  customQuotedPrice?: number | null
  estimatedPrice: number
  estimatedPricePerVisit?: number
}

const FinalPriceDifferenceIcon = ({ pricingType, customQuotedPrice, estimatedPrice, estimatedPricePerVisit }: FinalPriceDifferenceIconProps ) => {
  if (!hasValue(customQuotedPrice)) {
    return null
  }

  if (pricingType !== PricingTypes.QuotedPrice && pricingType !== PricingTypes.PerVisit) {
    return null
  }

  if (customQuotedPrice === estimatedPrice) {
    return null
  }

  const icon = customQuotedPrice > estimatedPrice ? faArrowCircleUp : faArrowCircleDown
  const estimatedAmount = pricingType === PricingTypes.PerVisit ? estimatedPricePerVisit ?? 0 : estimatedPrice

  return <Text type="secondary">
    <Tooltip title={`Overridden from ${centsToDollarsDisplay(estimatedAmount)}`} placement="top">
      <FontAwesomeIcon icon={icon} />
    </Tooltip>
  </Text>
}